import { ref } from 'vue';
import { useSessionExpiredStore } from '~/stores/useSessionExpiredStore';
import { useAuthStore } from '~/stores/useAuthStore';
import { appT } from '~/composables/useAppI18n';

/** 마지막 API 응답이 서버 기준 미인증(X-Authentication-Status: false)이었는지 */
export const lastResponseWasSessionExpired = ref(false);

export type CheckAuthFailureReason = 'session' | 'network' | 'invalid_response';

/**
 * 서버 AuthenticationStatusHeaderInterceptor 기준 미인증 여부
 * @see X-Authentication-Status: String.valueOf(isAuthenticated()) → "true" | "false"
 */
export const isSessionExpiredByHeader = (headers?: Headers | null): boolean => {
  return headers?.get('x-authentication-status') === 'false';
};

export const isLoginPath = (): boolean => {
  if (!process.client) return false;
  return window.location.pathname.includes('/login');
};

/** 세션 만료 확인 다이얼로그 표시 (로그인 페이지·미로그인 제외) */
export const showSessionExpiredModalIfNeeded = (): boolean => {
  if (isLoginPath()) return false;

  const authStore = useAuthStore();
  if (!authStore.isLoggedIn) return false;

  const sessionExpiredStore = useSessionExpiredStore();
  if (!sessionExpiredStore.isVisible) {
    sessionExpiredStore.show();
  }
  return true;
};

/**
 * 응답 헤더 기준 세션 만료 처리
 * @returns true면 세션 만료로 처리됨(호출측에서 추가 처리 중단)
 */
export const handleSessionExpiredFromHeaders = (headers?: Headers | null): boolean => {
  if (!isSessionExpiredByHeader(headers)) {
    lastResponseWasSessionExpired.value = false;
    return false;
  }

  lastResponseWasSessionExpired.value = true;
  showSessionExpiredModalIfNeeded();
  return true;
};

/** ofetch 요청 실패(네트워크·타임아웃·CORS 등) 여부 — HTTP 응답이 있으면 false */
export const isFetchNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return true;

  const err = error as {
    name?: string;
    code?: string;
    message?: string;
    response?: unknown;
  };

  if (err.response !== undefined && err.response !== null) {
    return false;
  }

  if (err.name === 'TimeoutError' || err.code === 'ETIMEDOUT') return true;

  return true;
};

export const getFetchNetworkErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object') {
    const err = error as { name?: string; code?: string };
    if (err.name === 'TimeoutError' || err.code === 'ETIMEDOUT') {
      return appT('common.api.requestTimeout');
    }
  }
  return appT('common.api.networkUnreachable');
};
